// src/lib/firebase.js
// PATCH (this version):
//   PHASE 5: firebaseConfig object extracted to src/config/firebase.config.js.
//   This file now imports from there — single source of truth for credentials.
//   All other logic is unchanged.
//
// ── PREVIOUS PATCHES (retained) ──────────────────────────────────────────────
//
//   FIX #3: _dbInitialised flag moved from module-level `let` to
//   `globalThis.__sheepheardDbInitialised`. Module-level variables are
//   re-evaluated on every HMR cycle in development, causing initializeFirestore
//   to run a second time on each hot reload and silently fall back to the
//   memory-only instance. `globalThis` is NOT reset by HMR, so the flag
//   survives hot reloads and StrictMode double-invokes correctly.

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// PHASE 5: credentials live in config/firebase.config.js — not here.
import { firebaseConfig } from '@/config/firebase.config';

// ── App init (hot-reload / StrictMode safe) ────────────────────────────────
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// ── Firestore with persistent offline cache ────────────────────────────────
//
// ROOT CAUSE FIX (v12 regression):
//
// In Firebase JS SDK v12, if ANY module calls getFirestore(app) before
// initializeFirestore(app, ...) runs — including within the same file via
// React StrictMode's double-invoke of module initializers — the SDK internally
// registers a *memory-only* Firestore instance first. The subsequent
// initializeFirestore() call then throws "Firestore has already been started"
// and our catch block falls through to getFirestore(app), which returns that
// already-registered *memory-only* instance. Result: zero offline persistence.
//
// FIX #3: Use globalThis.__sheepheardDbInitialised instead of a module-level
// `let` flag. Module-level variables reset on every HMR update in development
// mode, causing initializeFirestore to be called again on each hot reload.
// globalThis persists across HMR cycles (it is the window object), so the flag
// correctly prevents re-initialisation in both dev and production.
//
// TRADE-OFF of persistentSingleTabManager({ forceOwnership: true }):
//   Does NOT require BroadcastChannel — works in all PWA contexts including
//   iOS standalone, Android WebAPK, and private browsing. If two tabs are
//   open simultaneously each manages its own Firestore sync independently.
//   For a standalone PWA this is irrelevant (only one tab is open at a time).

let db;

if (!globalThis.__sheepheardDbInitialised) {
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        // Per firebase-church-skill: explicit cache size (50 MB)
        // Default is 40 MB; 50 MB gives headroom for offline-heavy use.
        cacheSizeBytes: 50 * 1024 * 1024,
        tabManager: persistentSingleTabManager({ forceOwnership: true }),
      }),
    });
    globalThis.__sheepheardDbInitialised = true;
    console.info('[FIREBASE] Firestore initialised with persistentLocalCache ✓');
  } catch (err) {
    // Only reached if initializeFirestore genuinely fails (e.g. storage blocked
    // in a privacy-hardened browser). Will NOT fire on HMR or StrictMode
    // double-invoke because the globalThis flag prevents re-entry.
    console.warn(
      '[FIREBASE] persistentLocalCache unavailable — falling back to memory-only Firestore.',
      'Offline data will NOT be cached across page reloads.',
      'This is expected in private/incognito mode or when storage is blocked.',
      err
    );
    db = getFirestore(app);
  }
} else {
  // Already initialised (HMR re-evaluation or StrictMode second invoke).
  // getFirestore() returns the persistentLocalCache instance because
  // globalThis.__sheepheardDbInitialised guarantees initializeFirestore
  // already succeeded on the first call.
  db = getFirestore(app);
}

// ── Auth — safe even when API key is missing/invalid ─────────────────────
let auth;
try {
  auth = getAuth(app);
} catch (err) {
  console.warn('[FIREBASE] Auth init failed (invalid or missing API key). App will run in offline-only mode.', err);
  auth = null;
}

export { db, auth };
export default app;